import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { SiteConfigManager, useApplicationState } from '@atlas/core';

import LobbyAnyCategoriesBlock from './LobbyAnyCategoriesBlock';
import LobbyGameTopCategoriesBlock from './LobbyGameTopCategoriesBlock';
import LobbyLastWinnersBlock from './LobbyLastWinnersBlock';
import LobbyPrimaryBlock from './LobbyPrimaryBlock';
import LobbyPromotionsBlock from './LobbyPromotionsBlock';
import LobbyProvidersBlock from './LobbyProvidersBlock';
import LobbyTournamentsBlock from './LobbyTournamentsBlock';

const LOBBY_TYPES = {
  ANY_CATEGORIES: 'ANY_CATEGORIES',
  LAST_WINNERS: 'LAST_WINNERS',
  PRIMARY: 'PRIMARY',
  PROMOTIONS: 'PROMOTIONS',
  PROVIDERS: 'PROVIDERS',
  TOP_GAMES: 'TOP_GAMES',
  TOURNAMENTS: 'TOURNAMENTS',
};

const BLOCK_COMPONENTS = {
  [LOBBY_TYPES.TOP_GAMES]: LobbyGameTopCategoriesBlock,
  [LOBBY_TYPES.PRIMARY]: LobbyPrimaryBlock,
  [LOBBY_TYPES.LAST_WINNERS]: LobbyLastWinnersBlock,
  [LOBBY_TYPES.TOURNAMENTS]: LobbyTournamentsBlock,
  [LOBBY_TYPES.PROMOTIONS]: LobbyPromotionsBlock,
  [LOBBY_TYPES.PROVIDERS]: LobbyProvidersBlock,
  [LOBBY_TYPES.ANY_CATEGORIES]: LobbyAnyCategoriesBlock,
};

const LobbyBlock = ({ authenticated, block, onProviderClick, setActiveItem }) => {
  if (!block || typeof block !== 'object') return null;

  const { settings = {}, type } = block;
  const typeUpper = (type || '').toUpperCase();

  if (!authenticated && [LOBBY_TYPES.TOURNAMENTS].includes(typeUpper)) {
    return null;
  }

  const Component = BLOCK_COMPONENTS[typeUpper];

  if (!Component) {
    console.warn(`Unknown Lobby Block type: ${type}`);
    return null;
  }

  if (typeUpper === LOBBY_TYPES.PROVIDERS) {
    return <Component {...settings} onProviderClick={onProviderClick} />;
  }

  if (typeUpper === LOBBY_TYPES.ANY_CATEGORIES || typeUpper === LOBBY_TYPES.TOP_GAMES) {
    return <Component {...settings} setActiveItem={setActiveItem} />;
  }

  return <Component {...settings} />;
};

LobbyBlock.propTypes = {
  authenticated: PropTypes.bool,
  block: PropTypes.object,
  onProviderClick: PropTypes.func,
  setActiveItem: PropTypes.func,
};

const sortLobbyStructure = lobbyStructure => {
  if (!lobbyStructure || !Array.isArray(lobbyStructure)) {
    return [];
  }

  const validBlocks = lobbyStructure.filter(block => block && typeof block === 'object');

  return validBlocks.sort((a, b) => {
    const aOrder = a?.orderPosition;
    const bOrder = b?.orderPosition;

    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }
    return 0;
  });
};

const CasinoLobby = ({ onProviderClick, setActiveItem }) => {
  const { authenticated } = useApplicationState();

  const casinoLobbyConfig = SiteConfigManager.getConfig('casino_lobby_config', {});

  const sortedStructure = useMemo(() => {
    return sortLobbyStructure(casinoLobbyConfig?.lobbyStructure);
  }, [casinoLobbyConfig]);

  return (
    <div className="casino-lobby">
      {sortedStructure.map(block => (
        <LobbyBlock
          authenticated={authenticated}
          block={block}
          key={block.id}
          onProviderClick={onProviderClick}
          setActiveItem={setActiveItem}
        />
      ))}
    </div>
  );
};

CasinoLobby.propTypes = {
  onProviderClick: PropTypes.func,
  setActiveItem: PropTypes.func,
};

export default CasinoLobby;
